import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

// Panel de sustituciones de asistentas (superadmin y admin_asistentas, solo lectura). Los datos
// llegan del Sheet "BAJAS Y SUST" por la sync; aquí se ve cuáles se aplican en la
// app, cuáles no y por qué, y el historial de cada una.

const ESTADOS = {
  en_vigor:   { label: "En vigor hoy", icono: "🟢", color: "#15803d", bg: "#dcfce7", borde: "#86efac" },
  futura:     { label: "Próximas",     icono: "🔵", color: "#1d4ed8", bg: "#dbeafe", borde: "#93c5fd" },
  incompleta: { label: "No se aplican", icono: "⚠️", color: "#b45309", bg: "#fef3c7", borde: "#fcd34d" },
  terminada:  { label: "Terminadas",   icono: "⚪", color: "#475569", bg: "#f1f5f9", borde: "#cbd5e1" },
  baja:       { label: "Eliminado",    icono: "🗑️", color: "#991b1b", bg: "#fee2e2", borde: "#fca5a5" },
};
const ORDEN_ESTADOS = ["en_vigor", "futura", "incompleta", "terminada", "baja"];

// Columnas de la tabla y cómo se ordena cada una (vacíos siempre al final)
const COLUMNAS = [
  { k: "estado", t: "Estado", v: (f) => ORDEN_ESTADOS.indexOf(f.estado) },
  { k: "fechas", t: "Fechas", v: (f) => f.fecha_inicio ? `${f.fecha_inicio}|${f.fecha_fin || "9999-99-99"}` : null },
  { k: "piso", t: "Piso", v: (f) => (f.piso || (f.id_piso != null ? `#${f.id_piso}` : null)) },
  { k: "titular", t: "Titular", v: (f) => f.titular || (f.id_titular != null ? `#${f.id_titular}` : null) },
  { k: "sustituta", t: "Sustituta", v: (f) => legible(f.nombre_sustituta) || (f.id_sustituta != null ? `#${f.id_sustituta}` : null) },
  { k: "horario", t: "Horario", v: (f) => f.hora_entrada || f.hora_salida || null },
  { k: "comentario", t: "Comentario", v: (f) => f.comentario || null },
];
const comparar = (a, b) => (typeof a === "number" && typeof b === "number" ? a - b : String(a).localeCompare(String(b), "es", { sensitivity: "base", numeric: true }));

const fmtFecha = (iso) => (iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}` : "");
const fmtFechaHora = (iso) => (iso ? `${fmtFecha(iso)} ${iso.slice(11, 16)}` : "");
const legible = (n) => {
  if (!n) return n;
  const [a, b] = String(n).split(",", 2).map((x) => x.trim());
  return b ? `${b} ${a}` : a;
};

const Chip = ({ estado }) => {
  const e = ESTADOS[estado] || ESTADOS.terminada;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 10px", borderRadius: 999, fontSize: 12, fontWeight: 800, whiteSpace: "nowrap", background: e.bg, color: e.color, border: `1px solid ${e.borde}` }}>
      {e.icono} {e.label}
    </span>
  );
};

const Persona = ({ nombre, id, user, activa, vacio = "—" }) => {
  if (!nombre && id == null) return <span style={{ color: "#cbd5e1" }}>{vacio}</span>;
  return (
    <div style={{ lineHeight: 1.3 }}>
      <div style={{ fontWeight: 700, color: "#1e293b" }}>{nombre || <span style={{ color: "#94a3b8" }}>sin nombre</span>}</div>
      <div style={{ fontSize: 11.5, color: "#64748b" }}>
        {id != null ? `#${id}` : "sin id"}
        {user ? <> · @{user}{activa === false && <span style={{ color: "#b91c1c", fontWeight: 700 }}> (inactiva)</span>}</>
          : id != null && <span style={{ color: "#b45309", fontWeight: 700 }}> · sin usuario</span>}
      </div>
    </div>
  );
};

const CAMPOS_ORIGINAL = [
  ["id_titular", "Id (titular)"], ["id_piso", "ID Piso"], ["fecha_inicio", "Inicio"], ["fecha_fin", "Fin"],
  ["id_sustituta", "ID Sust temporal"], ["nombre_sustituta", "Sustitución temporal"], ["horas", "horas"],
  ["hora_entrada", "hora entrada"], ["hora_salida", "hora salida"], ["comentario", "Comentario"],
];

const Detalle = ({ fila, onCerrar }) => {
  const [hist, setHist] = useState(null);
  useEffect(() => {
    setHist(null);
    axios.get(`/api/sustituciones/${fila.id}/historial`).then((r) => setHist(r.data || [])).catch(() => setHist([]));
  }, [fila.id]);
  return (
    <div onClick={onCerrar} style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.45)", zIndex: 1000, display: "flex", justifyContent: "flex-end" }}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: "min(560px, 100%)", height: "100%", background: "#fff", overflowY: "auto", padding: "18px 20px", boxSizing: "border-box", boxShadow: "-8px 0 30px rgba(0,0,0,0.2)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
          <h3 style={{ margin: 0, flex: 1, color: "#1e3a5f" }}>Sustitución #{fila.id}</h3>
          <Chip estado={fila.estado} />
          <button onClick={onCerrar} style={{ border: "none", background: "#f1f5f9", borderRadius: 8, padding: "6px 10px", cursor: "pointer", fontWeight: 800 }}>✕</button>
        </div>
        {fila.faltan.length > 0 && fila.estado !== "baja" && (
          <div style={{ background: "#fef3c7", border: "1px solid #fcd34d", color: "#92400e", borderRadius: 10, padding: "10px 12px", fontSize: 13.5, marginBottom: 12 }}>
            ⚠️ <strong>No se aplica en la app</strong>: falta {fila.faltan.join(", ")}.
          </div>
        )}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
          <div><div style={s.lbl}>Titular</div><Persona nombre={fila.titular} id={fila.id_titular} user={fila.titular_user} /></div>
          <div><div style={s.lbl}>Sustituta</div><Persona nombre={legible(fila.nombre_sustituta)} id={fila.id_sustituta} user={fila.sustituta_user} activa={fila.sustituta_activa} /></div>
          <div><div style={s.lbl}>Piso</div>{fila.piso ? <><strong>{fila.piso}</strong> <span style={{ color: "#64748b", fontSize: 12 }}>#{fila.id_piso}{fila.piso_activo === false && " (de baja)"}</span></> : fila.id_piso != null ? <span style={{ color: "#b91c1c" }}>#{fila.id_piso} no existe</span> : "—"}
            {fila.piso_supervisora && <div style={{ fontSize: 12, color: "#64748b" }}>👩‍💼 {fila.piso_supervisora}</div>}
          </div>
          <div><div style={s.lbl}>Fechas</div>{fmtFecha(fila.fecha_inicio) || "—"} → {fila.fecha_fin ? fmtFecha(fila.fecha_fin) : <em style={{ color: "#64748b" }}>sin fin</em>}</div>
          <div><div style={s.lbl}>Horario</div>{fila.hora_entrada || fila.hora_salida ? `${fila.hora_entrada || "--"} – ${fila.hora_salida || "--"}` : "el del piso"}{fila.horas && ` · ${fila.horas} h`}</div>
          <div><div style={s.lbl}>Comentario</div>{fila.comentario || "—"}</div>
        </div>
        <div style={s.lbl}>Tal como llegó del Sheet</div>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, marginBottom: 16 }}>
          <tbody>
            {CAMPOS_ORIGINAL.map(([k, t]) => (
              <tr key={k} style={{ borderBottom: "1px solid #f1f5f9" }}>
                <td style={{ padding: "5px 6px", color: "#64748b", width: 150 }}>{t}</td>
                <td style={{ padding: "5px 6px", fontFamily: "monospace" }}>{fila.datos_originales?.[k] || <span style={{ color: "#cbd5e1" }}>(vacío)</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div style={s.lbl}>Historial</div>
        {hist === null ? <div style={{ color: "#94a3b8", fontSize: 13 }}>Cargando…</div> : hist.length === 0 ? <div style={{ color: "#94a3b8", fontSize: 13 }}>Sin cambios registrados.</div> : (
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {hist.map((h, i) => (
              <div key={i} style={{ fontSize: 12.5, background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, padding: "6px 10px" }}>
                <strong style={{ color: h.tipo_evento === "BAJA" ? "#b91c1c" : h.tipo_evento === "ALTA" ? "#15803d" : "#1d4ed8" }}>{h.tipo_evento}</strong>
                {h.campo && <> · {h.campo}: <span style={{ color: "#94a3b8" }}>{h.valor_anterior ?? "—"}</span> → <strong>{h.valor_nuevo ?? "—"}</strong></>}
                <div style={{ color: "#94a3b8", fontSize: 11.5 }}>{fmtFechaHora(h.fecha_hora)} · {h.editor_email}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ── Aplicar cambios ─────────────────────────────────────────────────────
// Lo que llega del Sheet no tiene efecto en la app hasta que se aplica aquí.
const APLIC = {
  sin_aplicar: { txt: "Sin aplicar", icono: "⛔", fg: "#b91c1c", bg: "#fee2e2", fila: "#fff1f2", borde: "#ef4444" },
  cambios: { txt: "Cambios sin aplicar", icono: "✏️", fg: "#c2410c", bg: "#ffedd5", fila: "#fff7ed", borde: "#f97316" },
  se_quitara: { txt: "Se quitará al aplicar", icono: "🗑️", fg: "#991b1b", bg: "#fee2e2", fila: "#fef2f2", borde: "#b91c1c" },
};
const fmtValor = (campo, v) => {
  if (v == null || v === "") return "—";
  if (campo.startsWith("fecha")) return fmtFecha(v);
  if (campo.startsWith("hora") || campo === "horas") return String(v).slice(0, 5);
  if (campo === "nombre_sustituta") return legible(v);
  return String(v);
};
const enVigorHoy = (ini, fin, hoy) => !!ini && ini <= hoy && (!fin || fin >= hoy);

function AplicarCambios({ filas, hoy, onCerrar, onAplicado }) {
  const pendientes = useMemo(() => filas.filter((f) => f.aplicacion?.pendiente), [filas]);
  const [marcadas, setMarcadas] = useState(() => new Set(pendientes.map((f) => f.id)));
  const [enviando, setEnviando] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [error, setError] = useState("");
  const alternar = (id) => setMarcadas((m) => { const n = new Set(m); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const grupos = ["sin_aplicar", "cambios", "se_quitara"].map((k) => [k, pendientes.filter((f) => f.aplicacion.estado === k)]).filter(([, l]) => l.length);
  const aplicar = async () => {
    const items = pendientes.filter((f) => marcadas.has(f.id)).map((f) => ({ id: f.id, version: f.aplicacion.version }));
    if (!items.length) return;
    if (!window.confirm(`¿Aplicar ${items.length} cambio(s)? Se verán en la app de titulares, sustitutas y supervisoras, y se avisará de las que están en vigor hoy.`)) return;
    setEnviando(true); setError("");
    try {
      const r = await axios.post("/api/sustituciones/aplicar", { items });
      setResultado(r.data);
      onAplicado();
    } catch (e) {
      setError(e.response?.data?.error || "No se pudo aplicar.");
    }
    setEnviando(false);
  };
  return (
    <div onClick={onCerrar} style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)", zIndex: 4000, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "26px 10px", overflowY: "auto" }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 18, width: "min(820px, 100%)", padding: "18px 18px 16px", boxShadow: "0 24px 60px rgba(0,0,0,0.35)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
          <div style={{ flex: 1, fontSize: 20, fontWeight: 900, color: "#14532d" }}>✅ Aplicar cambios de sustituciones</div>
          <button onClick={onCerrar} style={{ border: "none", background: "#f1f5f9", borderRadius: 8, padding: "6px 10px", cursor: "pointer", fontWeight: 800 }}>✕</button>
        </div>
        <div style={{ fontSize: 13, color: "#64748b", marginBottom: 12 }}>
          Lo marcado pasa a verse en la app (titular, sustituta y supervisora). Se avisa (campana + móvil) solo de las que están <b>en vigor hoy</b>; las futuras y las terminadas se aplican sin aviso.
        </div>
        {error && <div style={{ background: "#fee2e2", color: "#991b1b", borderRadius: 10, padding: 10, marginBottom: 10 }}>{error}</div>}
        {resultado ? (
          <div style={{ background: "#dcfce7", border: "2px solid #22c55e", borderRadius: 14, padding: 14, color: "#14532d" }}>
            <div style={{ fontWeight: 900, fontSize: 16 }}>✅ Aplicado: {resultado.altas} nuevas · {resultado.cambios} cambios · {resultado.bajas} quitadas</div>
            <div style={{ marginTop: 4 }}>📣 {resultado.avisos} aviso(s) · {resultado.moviles_avisados} persona(s) avisadas al móvil{resultado.sustitutas_creadas ? ` · ${resultado.sustitutas_creadas} usuario(s) de sustituta creados` : ""}</div>
            {resultado.omitidas?.length > 0 && (
              <div style={{ marginTop: 8, color: "#9a3412" }}>No aplicadas: {resultado.omitidas.map((o) => `#${o.id}: ${o.motivo}`).join(" · ")}</div>
            )}
            {resultado.aviso_bajas && <div style={{ marginTop: 6, color: "#9a3412" }}>⚠️ {resultado.aviso_bajas}</div>}
            <button onClick={onCerrar} style={{ marginTop: 12, border: "none", borderRadius: 10, padding: "10px 18px", background: "#16a34a", color: "#fff", fontWeight: 800, cursor: "pointer" }}>Cerrar</button>
          </div>
        ) : pendientes.length === 0 ? (
          <div style={{ background: "#f0fdf4", borderRadius: 12, padding: 18, textAlign: "center", color: "#15803d", fontWeight: 700 }}>✅ Todo está aplicado: no hay cambios pendientes.</div>
        ) : (
          <>
            <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
              <button onClick={() => setMarcadas(new Set(pendientes.map((f) => f.id)))} style={st.mini}>☑ Marcar todas</button>
              <button onClick={() => setMarcadas(new Set())} style={st.mini}>☐ Desmarcar todas</button>
              <span style={{ marginLeft: "auto", fontSize: 13, color: "#475569", alignSelf: "center" }}>{marcadas.size} de {pendientes.length} marcadas</span>
            </div>
            <div style={{ maxHeight: "58vh", overflowY: "auto", display: "grid", gap: 10 }}>
              {grupos.map(([k, lst]) => (
                <div key={k}>
                  <div style={{ fontSize: 12.5, fontWeight: 900, color: APLIC[k].fg, textTransform: "uppercase", margin: "4px 2px 6px" }}>{APLIC[k].icono} {APLIC[k].txt} ({lst.length})</div>
                  <div style={{ display: "grid", gap: 6 }}>
                    {lst.map((f) => {
                      const a = f.aplicacion.aplicado;
                      const vigor = enVigorHoy(f.fecha_inicio, f.fecha_fin, hoy) || (a && enVigorHoy(a.fecha_inicio, a.fecha_fin, hoy));
                      const quien = [f.sustituta_user && `${f.sustituta_user} (sustituta)`, f.piso_supervisora && `${f.piso_supervisora} (supervisora)`].filter(Boolean);
                      return (
                        <label key={f.id} style={{ display: "flex", gap: 10, alignItems: "flex-start", border: `2px solid ${marcadas.has(f.id) ? APLIC[k].borde : "#e2e8f0"}`, background: marcadas.has(f.id) ? APLIC[k].fila : "#fff", borderRadius: 12, padding: "9px 11px", cursor: "pointer" }}>
                          <input type="checkbox" checked={marcadas.has(f.id)} onChange={() => alternar(f.id)} style={{ width: 18, height: 18, marginTop: 2 }} />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 800, color: "#1e293b", textDecoration: k === "se_quitara" ? "line-through" : "none" }}>
                              {f.piso || `#${f.id_piso}`} · {legible(f.nombre_sustituta) || "—"} sustituye a {f.titular || `#${f.id_titular}`}
                            </div>
                            <div style={{ fontSize: 12.5, color: "#475569", marginTop: 2 }}>
                              📅 {fmtFecha(f.fecha_inicio)} → {f.fecha_fin ? fmtFecha(f.fecha_fin) : "sin fin"}
                              {(f.hora_entrada || f.hora_salida) && ` · 🕐 ${(f.hora_entrada || "--").slice(0, 5)}–${(f.hora_salida || "--").slice(0, 5)}`}
                            </div>
                            {k === "cambios" && f.aplicacion.diferencias.map((d) => (
                              <div key={d.campo} style={{ fontSize: 12.5, marginTop: 3 }}>
                                <b>{d.etiqueta}:</b> <span style={{ background: "#fee2e2", color: "#991b1b", borderRadius: 6, padding: "0 5px", textDecoration: "line-through" }}>{fmtValor(d.campo, d.antes)}</span>
                                {" ➜ "}<span style={{ background: "#dcfce7", color: "#14532d", borderRadius: 6, padding: "0 5px", fontWeight: 700 }}>{fmtValor(d.campo, d.despues)}</span>
                              </div>
                            ))}
                            <div style={{ fontSize: 12, marginTop: 4, color: vigor ? "#1d4ed8" : "#94a3b8" }}>
                              {vigor ? `📣 En vigor hoy: avisará a ${quien.join(" · ") || "—"}` : "Sin aviso (no está en vigor hoy)"}
                            </div>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
            <button onClick={aplicar} disabled={enviando || !marcadas.size} style={{
              marginTop: 14, width: "100%", border: "none", borderRadius: 14, padding: "14px 0", fontWeight: 900, fontSize: 17, cursor: marcadas.size ? "pointer" : "default",
              background: marcadas.size ? "linear-gradient(135deg,#22c55e,#15803d)" : "#cbd5e1", color: "#fff", boxShadow: marcadas.size ? "0 6px 18px rgba(22,163,74,0.35)" : "none", opacity: enviando ? 0.7 : 1,
            }}>{enviando ? "Aplicando…" : `✅ Aplicar ${marcadas.size} cambio(s)`}</button>
          </>
        )}
      </div>
    </div>
  );
}
const st = { mini: { border: "1px solid #cbd5e1", background: "#fff", borderRadius: 8, padding: "5px 10px", fontSize: 12.5, fontWeight: 700, cursor: "pointer" } };

const SuperAdminSustituciones = () => {
  const navigate = useNavigate();
  const [datos, setDatos] = useState(null);
  const [error, setError] = useState("");
  const [estado, setEstado] = useState("en_vigor");
  const [busca, setBusca] = useState("");
  const [abierta, setAbierta] = useState(null);
  const [verAplicar, setVerAplicar] = useState(false);
  const [soloPendientes, setSoloPendientes] = useState(false);
  const [orden, setOrden] = useState({ k: "fechas", dir: 1 }); // por defecto: fecha de inicio ascendente
  const pulsarCabecera = (k) => setOrden((o) => (o.k === k ? { k, dir: -o.dir } : { k, dir: 1 }));

  const cargar = () => {
    setError("");
    axios.get("/api/sustituciones/").then((r) => setDatos(r.data)).catch((e) => setError(e.response?.data?.error || "No se pudieron cargar las sustituciones."));
  };
  useEffect(cargar, []);

  const filas = useMemo(() => datos?.filas || [], [datos]);
  const conteo = useMemo(() => {
    const m = {}; filas.forEach((f) => { m[f.estado] = (m[f.estado] || 0) + 1; }); return m;
  }, [filas]);
  const lista = useMemo(() => {
    const q = busca.trim().toLowerCase();
    return filas.filter((f) => (estado === "todas" || f.estado === estado) && (!soloPendientes || f.aplicacion?.pendiente) && (!q || [
      f.titular, f.nombre_sustituta, f.piso, f.id_titular, f.id_sustituta, f.id_piso, f.comentario, f.titular_user, f.sustituta_user, f.piso_supervisora,
    ].some((v) => v != null && String(v).toLowerCase().includes(q))));
  }, [filas, estado, busca, soloPendientes]);
  const pendientesEn = (k) => filas.filter((f) => (k === "todas" || f.estado === k) && f.aplicacion?.pendiente).length;
  const listaOrdenada = useMemo(() => {
    const col = COLUMNAS.find((c) => c.k === orden.k) || COLUMNAS[1];
    return [...lista].sort((a, b) => {
      const va = col.v(a), vb = col.v(b);
      const vaciaA = va == null || va === "", vaciaB = vb == null || vb === "";
      if (vaciaA || vaciaB) return vaciaA === vaciaB ? a.id - b.id : vaciaA ? 1 : -1;
      return comparar(va, vb) * orden.dir || a.id - b.id;
    });
  }, [lista, orden]);

  return (
    <div style={{ minHeight: "100vh", background: "#f1f5f9", fontFamily: "Segoe UI, sans-serif" }}>
      <div style={{ background: "#1e3a5f", color: "#fff", padding: "14px 18px", display: "flex", alignItems: "center", gap: 12 }}>
        <button onClick={() => navigate(-1)} style={{ background: "rgba(255,255,255,0.15)", border: "none", borderRadius: 8, color: "#fff", fontWeight: 700, cursor: "pointer", padding: "8px 14px" }}>← Volver</button>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, opacity: 0.7 }}>Gestionar datos</div>
          <strong style={{ fontSize: 20 }}>🔁 Sustituciones de asistentas</strong>
        </div>
        <button onClick={cargar} style={{ background: "rgba(255,255,255,0.15)", border: "none", borderRadius: 8, color: "#fff", fontWeight: 700, cursor: "pointer", padding: "8px 14px" }}>↻ Actualizar</button>
      </div>
      {verAplicar && datos && <AplicarCambios filas={filas} hoy={datos.hoy} onCerrar={() => setVerAplicar(false)} onAplicado={cargar} />}

      <div style={{ padding: "14px 16px", maxWidth: 1400, margin: "0 auto" }}>
        {error && <div style={{ background: "#fee2e2", color: "#991b1b", borderRadius: 10, padding: 12, marginBottom: 12 }}>{error}</div>}

        {/* Resumen = filtros */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 12 }}>
          {[...ORDEN_ESTADOS, "todas"].map((k) => {
            const e = ESTADOS[k] || { label: "Todas", icono: "📋", color: "#3730a3", bg: "#eef2ff", borde: "#c7d2fe" };
            const n = k === "todas" ? filas.length : (conteo[k] || 0);
            const activo = estado === k;
            return (
              <button key={k} onClick={() => setEstado(k)} style={{
                display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 2, minWidth: 130, padding: "10px 14px", borderRadius: 14, cursor: "pointer",
                background: activo ? e.bg : "#fff", border: `2px solid ${activo ? e.color : "#e2e8f0"}`, boxShadow: activo ? "0 4px 12px rgba(15,23,42,0.08)" : "none",
              }}>
                <span style={{ fontSize: 12.5, fontWeight: 700, color: e.color }}>{e.icono} {e.label}</span>
                <span style={{ fontSize: 24, fontWeight: 900, color: "#1e293b" }}>{datos ? n : "…"}</span>
                {datos && pendientesEn(k) > 0 && k !== "todas" && (
                  <span style={{ fontSize: 11.5, fontWeight: 800, color: "#b91c1c", background: "#fee2e2", borderRadius: 999, padding: "1px 8px" }}>⛔ {pendientesEn(k)} sin aplicar</span>
                )}
              </button>
            );
          })}
          {/* Aplicar cambios: a la derecha de las tarjetas, bien visible */}
          <button onClick={() => setVerAplicar(true)} style={{
            marginLeft: "auto", alignSelf: "center", display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "center", gap: 8, whiteSpace: "nowrap",
            border: "none", borderRadius: 15, cursor: "pointer", padding: "14px 18px", fontWeight: 900, fontSize: 16, color: "#fff",
            background: datos?.pendientes ? "linear-gradient(135deg,#22c55e,#15803d)" : "linear-gradient(135deg,#94a3b8,#64748b)",
            boxShadow: datos?.pendientes ? "0 0 0 4px rgba(134,239,172,0.6), 0 8px 22px rgba(22,163,74,0.45)" : "0 4px 12px rgba(15,23,42,0.15)",
          }}>
            <span>✅ Aplicar cambios</span>
            <span style={{ fontSize: 13, fontWeight: 800, background: "rgba(255,255,255,0.95)", color: datos?.pendientes ? "#15803d" : "#475569", borderRadius: 999, padding: "2px 12px" }}>
              {datos ? (datos.pendientes ? `${datos.pendientes} pendiente${datos.pendientes !== 1 ? "s" : ""}` : "todo aplicado") : "…"}
            </span>
          </button>
        </div>

        {/* Filtro "sin aplicar": debajo de las tarjetas, encima del buscador */}
        <div style={{ marginBottom: 10 }}>
            <button onClick={() => setSoloPendientes((v) => !v)} style={{
            border: `2px solid ${soloPendientes ? "#ef4444" : "#fecaca"}`, background: soloPendientes ? "#fee2e2" : "#fff", color: "#b91c1c",
            borderRadius: 12, padding: "8px 12px", fontWeight: 800, fontSize: 13, cursor: "pointer",
          }}>⛔ Solo sin aplicar / con cambios ({datos ? pendientesEn(estado) : "…"})</button>
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 10, flexWrap: "wrap" }}>
          <input value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="🔍 Buscar titular, sustituta, piso, supervisora, comentario…"
            style={{ flex: 1, minWidth: 240, padding: "10px 14px", borderRadius: 12, border: "2px solid #c7d2fe", fontSize: 14, outline: "none" }} />
          <span style={{ fontSize: 13, color: "#64748b" }}>{lista.length} sustitución{lista.length !== 1 ? "es" : ""}</span>
        </div>
        {estado === "incompleta" && (
          <div style={{ fontSize: 13, color: "#92400e", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 10, padding: "8px 12px", marginBottom: 10 }}>
            Estas están guardadas pero <strong>no tienen efecto</strong> en la app: les falta titular, piso, id o nombre de la sustituta, o la fecha de inicio (o el fin es anterior al inicio).
          </div>
        )}

        {!datos && !error ? <div style={{ textAlign: "center", padding: 40, color: "#64748b" }}>Cargando…</div> : (
          <div style={{ background: "#fff", borderRadius: 14, overflowX: "auto", boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13.5, minWidth: 900 }}>
              <thead>
                <tr style={{ background: "#3498db", color: "#fff" }}>
                  {COLUMNAS.map((c) => (
                    <th key={c.k} onClick={() => pulsarCabecera(c.k)} title="Pulsa para ordenar"
                      style={{ padding: "11px 10px", textAlign: "left", fontWeight: 700, whiteSpace: "nowrap", cursor: "pointer", userSelect: "none",
                        background: orden.k === c.k ? "#2a6fb0" : undefined }}>
                      {c.t}{c.k === "fechas" && " (inicio)"}{" "}
                      <span style={{ opacity: orden.k === c.k ? 1 : 0.4, fontSize: 11 }}>{orden.k === c.k ? (orden.dir === 1 ? "▲" : "▼") : "↕"}</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {lista.length === 0 ? (
                  <tr><td colSpan={7} style={{ padding: 30, textAlign: "center", color: "#94a3b8" }}>No hay sustituciones en este filtro.</td></tr>
                ) : listaOrdenada.map((f, i) => (
                  <tr key={f.id} onClick={() => setAbierta(f)} style={{
                      cursor: "pointer", background: APLIC[f.aplicacion?.estado]?.fila || (i % 2 ? "#fafafa" : "#fff"), borderBottom: "1px solid #eef2f7",
                      boxShadow: APLIC[f.aplicacion?.estado] ? `inset 5px 0 0 ${APLIC[f.aplicacion.estado].borde}` : "none",
                      opacity: f.aplicacion?.estado === "sin_aplicar" ? 0.8 : 1,
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = "#eef2ff"; }} onMouseLeave={(e) => { e.currentTarget.style.background = APLIC[f.aplicacion?.estado]?.fila || (i % 2 ? "#fafafa" : "#fff"); }}>
                    <td style={s.td}>
                      <Chip estado={f.estado} />
                      {APLIC[f.aplicacion?.estado] && (
                        <div style={{ marginTop: 4, display: "inline-block", fontSize: 11.5, fontWeight: 800, color: APLIC[f.aplicacion.estado].fg, background: APLIC[f.aplicacion.estado].bg, borderRadius: 999, padding: "2px 8px" }}>
                          {APLIC[f.aplicacion.estado].icono} {APLIC[f.aplicacion.estado].txt}
                        </div>
                      )}
                      {f.aplicacion?.estado === "cambios" && (
                        <div style={{ fontSize: 11.5, color: "#c2410c", marginTop: 3 }}>{f.aplicacion.diferencias.map((d) => d.etiqueta).join(", ")}</div>
                      )}
                      {f.estado === "incompleta" && <div style={{ fontSize: 11.5, color: "#b45309", marginTop: 3 }}>falta {f.faltan.join(", ")}</div>}
                    </td>
                    <td style={{ ...s.td, whiteSpace: "nowrap" }}>
                      {fmtFecha(f.fecha_inicio) || <span style={{ color: "#cbd5e1" }}>—</span>}
                      <div style={{ fontSize: 12, color: "#64748b" }}>{f.fecha_fin ? `→ ${fmtFecha(f.fecha_fin)}` : "→ sin fin"}</div>
                    </td>
                    <td style={s.td}>{f.piso ? <><strong>{f.piso}</strong><div style={{ fontSize: 11.5, color: "#64748b" }}>#{f.id_piso}{f.piso_supervisora ? ` · ${f.piso_supervisora}` : ""}</div></> : f.id_piso != null ? <span style={{ color: "#b91c1c" }}>#{f.id_piso} no existe</span> : <span style={{ color: "#cbd5e1" }}>—</span>}</td>
                    <td style={s.td}><Persona nombre={f.titular} id={f.id_titular} user={f.titular_user} /></td>
                    <td style={s.td}><Persona nombre={legible(f.nombre_sustituta)} id={f.id_sustituta} user={f.sustituta_user} activa={f.sustituta_activa} /></td>
                    <td style={{ ...s.td, whiteSpace: "nowrap" }}>{f.hora_entrada || f.hora_salida ? `${f.hora_entrada || "--"} – ${f.hora_salida || "--"}` : <span style={{ color: "#94a3b8" }}>el del piso</span>}{f.horas && <div style={{ fontSize: 12, color: "#64748b" }}>{f.horas} h</div>}</td>
                    <td style={{ ...s.td, maxWidth: 220, color: "#475569" }}>{f.comentario || ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {abierta && <Detalle fila={abierta} onCerrar={() => setAbierta(null)} />}
    </div>
  );
};

const s = {
  td: { padding: "9px 10px", verticalAlign: "top" },
  lbl: { fontSize: 11.5, fontWeight: 800, color: "#94a3b8", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 4 },
};

export default SuperAdminSustituciones;
